import React from 'react';
import i18n from '../../i18n';
import { appDateLocale } from '../../utils/appLocale';
import {
  UserPlus,
  Trash2,
  Shield,
  LayoutGrid,
  Activity,
  Link2,
  RefreshCw,
  Mail,
  Edit,
  Link2Off,
} from 'lucide-react';

// ─── time helpers ─────────────────────────────────────────────────────────────
/**
 * Relative "time ago" label.
 *
 * 30.08.2026: this used to return hardcoded `3h ago` and an `'en-GB'` fallback
 * date, and its OUTPUT is interpolated into `teamLibrary.updatedAgo`
 * ("Updated {{time}}" / "Aktualisiert {{time}}"). Filling that key without
 * touching this function ships **"Aktualisiert 3h ago"** — and the en/de
 * key-parity guard stays GREEN, because the key IS translated. A translated
 * label can wrap an untranslated value; parity cannot see it. Whenever you fill
 * a key containing a `{{placeholder}}`, read what PRODUCES the placeholder.
 * `inheritedGermanKeys.test.js` pins this, and pins that parity stays green.
 *
 * Module scope, so it reads the i18n singleton (the `formatHistoryTime.js` /
 * `History.js` precedent) — resolved at CALL time, so it follows a language
 * switch. `shortAgo()` below and the other date-locale sites in this file are
 * deliberately OUT of scope; they are not reached by any of the keys this task
 * filled and are chipped as a separate app-wide sweep.
 */
export function ago(d) {
  if (!d) return null;
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return i18n.t('common.agoSeconds', { n: s });
  if (s < 3600) return i18n.t('common.agoMinutes', { n: Math.floor(s / 60) });
  if (s < 86400) return i18n.t('common.agoHours', { n: Math.floor(s / 3600) });
  if (s < 7 * 86400) return i18n.t('common.agoDays', { n: Math.floor(s / 86400) });
  return new Date(d).toLocaleDateString(appDateLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function shortAgo(d) {
  if (!d) return '—';
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}d`;
}

// ─── Activity helpers ─────────────────────────────────────────────────────────
export function activityDesc(a, T) {
  const m = a.metadata || {};
  const actor = a.user?.name || 'Someone';
  const b = s => <strong style={{color:T.text}}>{s}</strong>;
  switch (a.type) {
    case 'member_joined':      return <>{b(m.member_name||actor)} <span>joined the team</span></>;
    case 'member_invited':     return <>{b(actor)} <span>invited </span><span style={{color:T.textSub}}>{m.email}</span></>;
    case 'role_changed':       return <>{b(actor)}<span> changed </span>{b(m.member_name)}<span> from </span><span style={{color:'#93c5fd'}}>{m.from_role}</span><span> to </span><span style={{color:'var(--accent)'}}>{m.to_role}</span></>;
    case 'workspace_linked':
    case 'workspace_shared':   return <>{b(actor)}<span> shared workspace </span>{b(m.workspace_name)}</>;
    case 'workspace_unlinked':
    case 'workspace_removed':
    case 'workspace_removed_from_team': return <>{b(actor)}<span> removed workspace </span>{b(m.workspace_name)}</>;
    case 'team_renamed':       return <>{b(actor)}<span> renamed the team to </span>{b(m.to)}</>;
    case 'team_updated':       return <>{b(actor)}<span> updated team settings</span></>;
    case 'member_removed':        return <>{b(actor)}<span> removed </span>{b(m.member_name)}</>;
    case 'share_link_generated':  return <>{b(actor)}<span> generated a share link</span></>;
    case 'invite_code_regenerated': return <>{b(actor)}<span> regenerated the invite code</span></>;
    default:                      return <>{b(actor)}<span> performed an action</span></>;
  }
}

export function activityIcon(type) {
  switch (type) {
    case 'member_joined':      return UserPlus;
    case 'member_invited':     return Mail;
    case 'role_changed':       return Shield;
    case 'workspace_linked':
    case 'workspace_shared':   return LayoutGrid;
    case 'workspace_unlinked':
    case 'workspace_removed':
    case 'workspace_removed_from_team': return Link2Off;
    case 'team_renamed':
    case 'team_updated':       return Edit;
    case 'member_removed':        return Trash2;
    case 'share_link_generated':  return Link2;
    case 'invite_code_regenerated': return RefreshCw;
    default:                      return Activity;
  }
}

export function activityStatSub(a) {
  if (!a) return 'No activity';
  const m = a.metadata || {};
  switch (a.type) {
    case 'workspace_shared':
    case 'workspace_linked':
      return m.workspace_name ? `Shared ${m.workspace_name}` : 'Workspace shared';
    case 'workspace_removed':
    case 'workspace_unlinked':
    case 'workspace_removed_from_team':
      return m.workspace_name ? `Removed ${m.workspace_name}` : 'Workspace removed';
    case 'member_joined':
      return m.member_name ? `${m.member_name} joined` : 'Member joined';
    case 'member_invited':
      return m.email ? `Invited ${m.email}` : 'Member invited';
    case 'member_removed':
      return m.member_name ? `Removed ${m.member_name}` : 'Member removed';
    case 'role_changed':
      return m.member_name ? `${m.member_name} role changed` : 'Role changed';
    case 'team_renamed':
      return m.to ? `Renamed to ${m.to}` : 'Team renamed';
    case 'team_updated':
      return 'Team settings updated';
    case 'share_link_generated':
      return 'Share link generated';
    case 'invite_code_regenerated':
      return 'Invite code regenerated';
    default:
      return (a.type || 'activity').replace(/_/g, ' ');
  }
}

export function groupByDate(list) {
  const now = Date.now();
  const groups = {};
  for (const a of list) {
    const diff = Math.floor((now - new Date(a.created_at)) / 86400000);
    const label = diff===0?'TODAY':diff===1?'YESTERDAY':diff<=7?'THIS WEEK':'EARLIER';
    (groups[label] = groups[label]||[]).push(a);
  }
  return groups;
}

export const GROUP_ORDER = ['TODAY','YESTERDAY','THIS WEEK','EARLIER'];
